import type { CustomValidator, UiSchema } from '@rjsf/utils';
import type { LinkRow, MenuItem } from './MenuBuilderCore';

const TITLE_REQUIRED_MESSAGE = 'Title is required';

interface ValidationNode {
  addError: (message: string) => void;
  [key: string]: ValidationNode | ((message: string) => void);
}

const childNode = (node: ValidationNode | undefined, key: string | number): ValidationNode | undefined => {
  const child = node?.[String(key)];
  return typeof child === 'function' ? undefined : child;
};

const validateRow = (row: LinkRow, node: ValidationNode | undefined): void => {
  if (!node || row.isReadOnly) return;
  if (!row.label?.trim()) childNode(node, 'label')?.addError(TITLE_REQUIRED_MESSAGE);
};

const validateMenuItems = (items: MenuItem[], node: ValidationNode | undefined): void => {
  items.forEach((item, i) => {
    const itemNode = childNode(node, i);
    if (!itemNode) return;
    if (item.kind === 'group') {
      if (!item.label?.trim()) childNode(itemNode, 'label')?.addError(TITLE_REQUIRED_MESSAGE);
      const linksNode = childNode(itemNode, 'links');
      item.links.forEach((link, li) => validateRow(link, childNode(linksNode, li)));
    } else {
      validateRow(item, itemNode);
    }
  });
};

const walkUiSchema = (ui: UiSchema, data: unknown, node: ValidationNode | undefined): void => {
  if (!node || !data || typeof data !== 'object') return;
  Object.entries(ui).forEach(([key, childUi]) => {
    if (key.startsWith('ui:') || !childUi || typeof childUi !== 'object') return;
    const childData = (data as Record<string, unknown>)[key];
    if ((childUi as UiSchema)['ui:widget'] === 'menuBuilder') {
      if (Array.isArray(childData)) {
        validateMenuItems(childData as MenuItem[], childNode(node, key));
      }
      return;
    }
    walkUiSchema(childUi as UiSchema, childData, childNode(node, key));
  });
};

// JSON Schema `required` only checks key presence, so a menu item with an empty
// string title passes AJV. Enforce non-empty titles for every menuBuilder
// field found in the uiSchema.
export function createMenuBuilderValidate(uiSchema: UiSchema): CustomValidator {
  return (formData, errors) => {
    walkUiSchema(uiSchema, formData, errors as unknown as ValidationNode);
    return errors;
  };
}
